import { ELanguage } from '@microfrontends/app-shell-v2'
import Image from 'next/image'
import { useContext, useEffect, useState } from 'react'
import AppContext from '../../context/AppContext'
import {
	checkIsNew,
	formatUpdatedDate,
	remainingDayNode
} from '../../helpers/formatUpdatedDate'
import {
	defaultLogo,
	EPageTab,
	IJobItem,
	TSkills
} from '../../services/Jobs/@type'
import BenefitTag from '../BenefitTag'
import {
	badgeIcons,
	buildJobsByCompanyUrl,
	generateJobUrl,
	listBenefits
} from './utilJob'
import SkillTag from '../SkillTag'
import JobsResultCss from './JobsResult.css'
import { formatSalary } from '../../services/Jobs/helper'
import { Tooltip, Utils } from '@microfrontends/react-components'
import SaveLoadingIcon from './SaveLoadingIcon'
import JobsSearchApi from '../../services/Jobs'
import { isMobile } from '../utils'
import { useRouter } from 'next/router'
import { showLoginModal } from '../../helpers/buildLoginUrl'

interface IProps {
	job: IJobItem
	pageTab?: EPageTab
	isRobot?: boolean
	utm?: string
}

const SERVICE_CODE_URGENT_JOB = 'UJ'
const SERVICE_CODE_TOP_MANAGEMENT_JOB = 'TMJ'
const SERVICE_CODE_HEADHUNT_JOB = 'JPHH'
const SERVICE_CODE_HEADHUNT_JOB_MOBILE = 'JPHM'

export default function JobsResult({ job, pageTab, isRobot, utm }: IProps) {
	const { t, languageCode, user } = useContext(AppContext)
	const [showTooltip, setShowTooltip] = useState<boolean | null>(null)
	const router = useRouter()
	const [showTooltipSkill, setShowTooltipSkill] = useState<boolean | null>(
		null
	)
	const [loading, setLoading] = useState<boolean | null>(null)
	const [showMatching, setShowMatching] = useState<boolean | null>(null)
	const [isSaved, setIsSaved] = useState<boolean>(false)
	const formattedDate = formatUpdatedDate(job, t)
	const { postedDate, remainingDay } = formattedDate
	const jobUrl = generateJobUrl(
		job?.alias ?? '',
		job.jobId.toString(),
		languageCode as string,
		utm
	)
	const companyUrl = buildJobsByCompanyUrl(
		job.companyId,
		job.companyName,
		languageCode ?? ELanguage.VI
	)
	const badgeSettings = {
		premium: {
			icon: badgeIcons.premium,
			name: t('Premium'),
			url: '',
			width: 92,
			cssClass: 'red'
		},
		urgent: {
			icon: badgeIcons.urgent,
			name: t('Urgent'),
			url: '',
			width: 75,
			cssClass: 'red'
		},
		topManagement: {
			icon: badgeIcons.topManagement,
			name: t('Top Management'),
			url: `${t(
				'/management-jobs'
			)}/?utm_source_navi=vnw_searchresults&utm_medium_navi=TopManagementJobs`,
			width: 138,
			cssClass: 'blue'
		},
		headHunter: {
			icon: badgeIcons.headHunter,
			name: t('Head Hunter'),
			url: '',
			width: 112,
			cssClass: 'blue'
		}
	}

	const renderBadges = () => {
		let {
			serviceCode,
			isPremium,
			isUrgentJob,
			isTopManagementJob,
			isHeadhuntJob
		} = job
		const isOnNormalJobPage = pageTab === EPageTab.JOBS
		const badges: any = []
		if (isRobot) {
			isUrgentJob = (serviceCode && serviceCode.indexOf(SERVICE_CODE_URGENT_JOB) > -1) || false
			isTopManagementJob =  (serviceCode && serviceCode.indexOf(SERVICE_CODE_TOP_MANAGEMENT_JOB) > -1) || false
			isHeadhuntJob = (serviceCode && (serviceCode?.indexOf(SERVICE_CODE_HEADHUNT_JOB) > -1 ||
				serviceCode?.indexOf(SERVICE_CODE_HEADHUNT_JOB_MOBILE) > -1)) || false

		}
		if (isUrgentJob) {
			badges.push(badgeSettings.urgent)
		}
		if (!isOnNormalJobPage) {
			if (isPremium) {
				badges.push(badgeSettings.premium)
			}

			if (isHeadhuntJob) {
				badges.push(badgeSettings.headHunter)
			}
			if (isTopManagementJob) {
				badges.push(badgeSettings.topManagement)
			}
		}
		let nextRightPos = -10
		if (badges.length === 0) {
			return <></>
		}
		return (
			<div className='wrap-badge reverted-order'>
				{badges.map((badge, index) => {
					const rightPos = nextRightPos
					nextRightPos += badge.width + 8
					return (
						<span
							key={index}
							className={`wrap-badge__badge wrap-badge__badge-${badge.cssClass}`}
							style={{
								right: rightPos,
								zIndex: badges.length - index
							}}
						>
							{badge.url !== '' ? (
								<a href={badge.url} title={badge.name}>
									{badge.icon}
									{badge.name}
								</a>
							) : (
								<>
									{badge.icon}
									{badge.name}
								</>
							)}
						</span>
					)
				})}
			</div>
		)
	}

	const handleMatchedBadgeActions = (type: string) => {
		switch (type) {
			case 'click':
				setShowMatching(!showTooltip)
				break
			case 'mouseEnter':
				setShowMatching(true)
				break
			case 'mouseLeave':
				setShowMatching(false)
				break
			default:
		}
	}

	const renderMatchTooltipBadge = () => {
		const text = `${t(
			'This job is consistent with the following criteria in your profile'
		)}: ${t('Title')}, ${t('Workplace')}, ${t('Skills')}, ${t('Category')}`
		return job?.isMatching ? (
			<div
				className='job__tool-item --blue'
				onMouseEnter={() => handleMatchedBadgeActions('mouseEnter')}
				onMouseLeave={() => handleMatchedBadgeActions('mouseLeave')}
				onClick={(e) => handleMatchedBadgeActions('click')}
			>
				<span>{t('Matching')}</span>
				<Tooltip
					isShow={showMatching ?? false}
					customStyle={{
						width: '220px',
						padding: '5px'
					}}
					placement={'bottom'}
					description={text}
				/>
			</div>
		) : null
	}

	const onClickLogin = (e: React.MouseEvent) => {
		e.preventDefault()
		showLoginModal(languageCode || ELanguage.VI)
	}

	const renderAppliedTextAndSaveIcon = () => {
		const { isApplied } = job
		const LoginAlert = () => {
			return (
				<>
					{t('You should')}{' '}
					<a href={loginURL} onClick={onClickLogin}>
						{t('Sign In')}{' '}
					</a>
					{t('to save jobs')}
				</>
			)
		}
		const loginURL =
			languageCode === 'en'
				? `${process.env.VNW_BASE_URL}/login?redirectURL=${router.pathname}&type=login`
				: `${process.env.VNW_BASE_URL}/dang-nhap?redirectURL=${router.pathname}&type=login`
		return (
			<div className='job__tool-item --green'>
				{isApplied ? (
					<span>{t('Applied')}</span>
				) : (
					<>
						{!user ? (
							<Tooltip
								isShow={showTooltip ?? false}
								customStyle={{
									width: 'max-conttent',
									padding: '10px'
								}}
								placement={'left'}
								description={<LoginAlert />}
							/>
						) : null}
						{!loading ? (
							<svg
								style={{ opacity: isSaved ? 1 : 0.5 }}
								fill='#000000'
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 50 50'
								width='20px'
								height='20px'
								onClick={() => {
									if (user) {
										handleBookmarkJobById()
									}
								}}
							>
								{isSaved ? (
									<path
										fill='#669CFF'
										d='M 25 47.300781 L 24.359375 46.769531 C 23.144531 45.753906 21.5 44.652344 19.59375 43.378906 C 12.167969 38.40625 2 31.601563 2 20 C 2 12.832031 7.832031 7 15 7 C 18.894531 7 22.542969 8.734375 25 11.699219 C 27.457031 8.734375 31.105469 7 35 7 C 42.167969 7 48 12.832031 48 20 C 48 31.601563 37.832031 38.40625 30.40625 43.378906 C 28.5 44.652344 26.855469 45.753906 25.640625 46.769531 Z'
									/>
								) : (
									<path d='M 15 7 C 7.832031 7 2 12.832031 2 20 C 2 34.761719 18.695313 42.046875 24.375 46.78125 L 25 47.3125 L 25.625 46.78125 C 31.304688 42.046875 48 34.761719 48 20 C 48 12.832031 42.167969 7 35 7 C 30.945313 7 27.382813 8.925781 25 11.84375 C 22.617188 8.925781 19.054688 7 15 7 Z M 15 9 C 18.835938 9 22.1875 10.96875 24.15625 13.9375 L 25 15.1875 L 25.84375 13.9375 C 27.8125 10.96875 31.164063 9 35 9 C 41.085938 9 46 13.914063 46 20 C 46 32.898438 31.59375 39.574219 25 44.78125 C 18.40625 39.574219 4 32.898438 4 20 C 4 13.914063 8.914063 9 15 9 Z' />
								)}
							</svg>
						) : (
							<SaveLoadingIcon />
						)}
					</>
				)}
			</div>
		)
	}
	const renderToolTipSkillTags = (skills: TSkills[] | null) => {
		if (!skills) {
			return
		}
		const text: string[] = []
		skills.forEach((skill, i) => {
			if (i > 2) {
				text.push(skill.skillName)
			}
		})
		return (
			<Tooltip
				isShow={showTooltipSkill ?? false}
				customStyle={{
					width: 'max-content',
					padding: '5px 10px'
				}}
				placement={'bottom'}
				description={<p>{text.join(', ')}</p>}
			/>
		)
	}
	const handleBookmarkJobById = () => {
		setLoading(true)
		;(async () => {
			if (!user?.accessToken) return
			try {
				const boomark = await JobsSearchApi.updateBookmarkJobsById(
					job.jobId,
					isSaved,
					user?.accessToken
				)
				if (boomark) {
					if (isSaved) {
						Utils.showToast(
							t('Success'),
							t('Just removed 1 save job')
						)
					} else {
						// eslint-disable-next-line @next/next/no-html-link-for-pages
						Utils.showToast(
							t('Success'),
							<div>
								{t('You have saved job, view')}{' '}
								<a
									href={t(
										'/my-career-center/my-jobs?section=saved'
									)}
								>
									{t('My jobs')}
								</a>
							</div>
						)
					}
					setIsSaved(!isSaved)
					setTimeout(() => {
						setLoading(false)
					}, 1000)
				}
			} catch (error) {
				console.error(error)
			}
		})()
	}
	useEffect(() => {
		if (job.isSaved) {
			setIsSaved(job.isSaved)
		}
	}, [job.isSaved])
	const checkSize = isMobile()
	return (
		<JobsResultCss>
			<div
				className={`wrap view_job_item imp-click recommendation_jobs ${
					job.isTopLevelJob ? 'priority' : ''
				}`}
			>
				{!checkSize ? null : renderBadges()}

				<div className='job'>
					{checkSize ? null : (
						<div className='job__salary'>
							{job?.prettySalary ?? ''}
						</div>
					)}
					<div className='job__image'>
						<div className='image'>
							<Image
								src={
									!job?.companyLogo
										? defaultLogo
										: job?.companyLogo
								}
								width={117}
								height={117}
								objectFit='contain'
								alt='best jobs'
							/>
						</div>
					</div>
					<div className='job__info'>
						<a
							href={jobUrl ?? ''}
							title={job?.jobTitle ?? ''}
							className={`job__info-title ${
								job.isBoldAndRedJob || job.visibilityDisplay
									? '--red'
									: ''
							}`}
							target='_self'
						>
							{job?.jobTitle ?? ''}
							{checkIsNew(job) && (
								<sup className='new'>({t('New')})</sup>
							)}
						</a>
						{checkSize ? null : renderBadges()}
						<a href={companyUrl} className='job__info-subTitle'>
							{job?.companyName ?? ''}
						</a>
						{job?.workingLocations &&
						job?.workingLocations.length > 0
							? job?.workingLocations?.map((elm) => {
									return (
										<p
											className='job__info-text'
											key={elm?.workingLocationId}
										>
											{languageCode === ELanguage.VI
												? elm?.cityNameVI
												: elm?.cityName}
										</p>
									)
							  })
							: null}
						<p
							className='job__info-text'
							dangerouslySetInnerHTML={{
								__html: `<span>${t(
									'Updated'
								)}: ${postedDate} - ${remainingDayNode(
									remainingDay,
									t
								)}</span>`
							}}
						/>
						{!checkSize ? null : (
							<div className='job__info-text salary'>
								{formatSalary(job, t)}
							</div>
						)}

						<div className='job__info-tags benefits-gap'>
							{job?.benefits && job?.benefits.length > 0
								? job?.benefits.map((item, i) => {
										return (
											<BenefitTag
												icon={listBenefits
													.filter(
														(elm) =>
															elm?.id ===
															item?.benefitId
													)[0]
													.icon()}
												name={
													languageCode ===
													ELanguage.VI
														? item?.benefitNameVI
														: item?.benefitName
												}
												key={item?.benefitId}
											/>
										)
								  })
								: null}
						</div>
						<div className='job__info-tags skills-gap block-bottom'>
							<div className='block-bottom__skill'>
								{job?.skills && job?.skills.length > 0
									? job?.skills.map((item, i) => {
											if (!isMobile() && i > 2) {
												return
											}
											return (
												<SkillTag
													key={item?.skillId}
													name={item?.skillName}
												/>
											)
									  })
									: null}
								{!isMobile() &&
								job?.skills &&
								job?.skills.length > 3 ? (
									<div
										className='--relative'
										onMouseEnter={() =>
											setShowTooltipSkill(
												!showTooltipSkill
											)
										}
										onMouseLeave={() =>
											setShowTooltipSkill(
												!showTooltipSkill
											)
										}
									>
										<SkillTag
											name={`+${job?.skills.length - 3}`}
										/>
										{renderToolTipSkillTags(job?.skills)}
									</div>
								) : null}
							</div>
							<div className='block-bottom__tool'>
								<div className='job__tool'>
									<div className='job__tool-mathching'>
										{renderMatchTooltipBadge()}
									</div>
									<div
										className='bookmark'
										onMouseEnter={() =>
											setShowTooltip(!showTooltip)
										}
										onMouseLeave={() =>
											setShowTooltip(!showTooltip)
										}
									>
										{renderAppliedTextAndSaveIcon()}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</JobsResultCss>
	)
}
